import { inferTripTypeFromAirportSide } from '@ixigo/cab-core/location';
import { toast } from '@ixigo/iui/Toast';
import { useElementOnScreen } from '@repo/shared/hooks';
import { useEffect, useRef, useState } from 'react';
import {
	type FormValidationResult,
	NavigationHeader,
	SearchForm,
	type SearchFormData,
} from '@/components';
import { CabSelectionWithData } from '@/components/cab-selection';
import {
	getLocationsFromIdentifiers,
	loadSearchFormData,
	SearchBar,
	saveSearchFormData,
	searchTracker,
	startLegacyMigration,
	useSearchFormData,
} from '@/components/search-form';
import { useSearchFormActions } from '@/components/search-form/store';
import { TrackerProvider } from '@/lib/analytics/provider';
import { useNavigate } from '@/lib/navigation';
import { useModalState } from '@/lib/navigation/useModalState';
import { serializeRouteParams } from '@/utils/routeParams';
import { coerceToValidDateTime, parseUrlPrefillParams } from '../-utils';
import { SearchBanner } from './search-banner';

const CAB_SEARCH_FORM_SELECTOR = '#cab-search-form';
const CAB_SEARCH_SECTION_SELECTOR = '#cab-search-section';

export const SearchSection = () => {
	const navigate = useNavigate();
	const searchFormActions = useSearchFormActions();
	// Shared with SearchForm: opens the location picker on a given field
	const { setOpen: setLocationPicker } = useModalState('loc');
	const [cabSelectionData, setCabSelectionData] =
		useState<Required<SearchFormData> | null>(null);
	const searchFormData = useSearchFormData();
	const urlPrefillProcessedRef = useRef(false);

	// Load saved search form data on mount OR prefill from URL params
	useEffect(() => {
		// Skip if already processed URL prefill
		if (urlPrefillProcessedRef.current) {
			return;
		}

		const urlParams = new URLSearchParams(window.location.search);
		const prefillParams = parseUrlPrefillParams(urlParams);

		// If URL has prefill params, use them instead of saved data
		if (prefillParams && (prefillParams.pickup || prefillParams.drop)) {
			urlPrefillProcessedRef.current = true;

			// Fetch location details from coordinates or placeId
			getLocationsFromIdentifiers({
				pickup: prefillParams.pickup,
				drop: prefillParams.drop,
			}).then(({ pickup, drop }) => {
				const formUpdate: Partial<SearchFormData> = {};

				if (pickup) {
					formUpdate.pickup = pickup;
				}
				if (drop) {
					formUpdate.drop = drop;
				}
				// Trip-type inference from a backend-tagged airport side
				// (FLIG-22203); only the type-tag path is reliable without a
				// nearest-airport endpoint. Leaves type untouched if inconclusive.
				const inferredTripType = inferTripTypeFromAirportSide(
					pickup?.type,
					drop?.type,
				);
				if (inferredTripType) {
					formUpdate.tripType = inferredTripType;
				}
				if (prefillParams.time) {
					formUpdate.dateTime = coerceToValidDateTime(prefillParams.time, 30);
				}

				if (Object.keys(formUpdate).length > 0) {
					searchFormActions.updateFormData(formUpdate);
				}
			});
			return;
		}

		// No URL params, load from localStorage.
		urlPrefillProcessedRef.current = true;
		const adjustDateTime = (dateTime?: string) =>
			coerceToValidDateTime(dateTime, 30);

		const savedFormData = loadSearchFormData();
		if (savedFormData) {
			searchFormActions.updateFormData({
				...savedFormData,
				dateTime: adjustDateTime(savedFormData.dateTime),
			});
			return;
		}

		// Legacy / shape-mismatched save? Try the BE-driven migration.
		// Returns the abort cleanup we wire to unmount.
		return startLegacyMigration(searchFormActions, adjustDateTime);
	}, [searchFormActions]);

	const isCabSearchFormOnScreen = useElementOnScreen(
		CAB_SEARCH_FORM_SELECTOR,
		CAB_SEARCH_SECTION_SELECTOR,
		{
			threshold: 0.25,
		},
	);

	const handleBackClick = () => {
		navigate({
			back: 'exit',
			href: '/',
			replace: true,
			reloadDocument: true,
		});
	};

	const handleSubmit = async (validationResult: FormValidationResult) => {
		if (!validationResult.isValid) {
			toast.error(validationResult.errorMessage, {
				position: 'bottom-center',
				duration: 2000,
			});
			return;
		}
		saveSearchFormData(validationResult.data);
		setCabSelectionData(validationResult.data);
	};

	const handleSelectionClose = () => {
		setCabSelectionData(null);
	};

	const handleSelectionConfirm = (cabId: string, acceptedActions: string[]) => {
		// for the routeParams, we only need
		const routeParams = serializeRouteParams({
			...{
				...searchFormData,
				pickup: {
					lat: searchFormData.pickup?.lat,
					lng: searchFormData.pickup?.lng,
					placeId: searchFormData.pickup?.placeId,
				},
				drop: {
					lat: searchFormData.drop?.lat,
					lng: searchFormData.drop?.lng,
					placeId: searchFormData.drop?.placeId,
				},
			},
			cabId,
			// Forward any accepted popup actions so the review fetch sends them
			// up front instead of re-triggering the near-airport confirmation.
			...(acceptedActions.length ? { actions: acceptedActions } : {}),
		});
		navigate({
			to: '/review',
			search: routeParams,
			viewTransition: {
				types: ['slide-left'],
			},
		});
	};

	const handleSearchClick = () => {
		document.getElementById('cab-search-section')?.scrollIntoView({
			behavior: 'smooth',
			block: 'center',
		});
	};

	return (
		<TrackerProvider track={searchTracker.track}>
			<div
				className="flex flex-col relative bg-primary p-20"
				id="cab-search-section"
			>
				<div
					className={`fixed top-0 left-0 z-10 bg-primary w-full transition-[border,box-shadow,transform] duration-300 ease-out ${
						!isCabSearchFormOnScreen
							? 'border-b border-tertiary shadow-100 translate-y-0'
							: 'border-b-none shadow-none -translate-y-full'
					}`}
				>
					<NavigationHeader
						title="Airport Cabs"
						leftAction={handleBackClick}
						className="relative"
					/>
					<div className="px-20 pb-10">
						<SearchBar
							searchValue={searchFormData.pickup?.mainText}
							onSearchClick={handleSearchClick}
						/>
					</div>
				</div>
				<SearchBanner onBack={handleBackClick} />
				<SearchForm
					onSubmit={handleSubmit}
					id="cab-search-form"
					className="relative mt-100px z-[1] bg-primary rounded-20"
				/>
				<CabSelectionWithData
					showOptions={!!cabSelectionData}
					onClose={handleSelectionClose}
					onConfirm={handleSelectionConfirm}
					formData={cabSelectionData}
					onNearbyAirportEdit={(field) => {
						// Re-open the picker on the field the user wants to change
						setLocationPicker({ open: true, value: field });
					}}
				/>
			</div>
		</TrackerProvider>
	);
};
